import { QueryClient, QueryCacheNotifyEvent, MutationCacheNotifyEvent } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { normalizeError, ErrorCategory } from './error/errorNormalizer';

/**
 * Default error handler for query/mutation errors
 */
export const defaultQueryErrorHandler = (error: unknown) => {
  // Normalize the error to a consistent format
  const normalizedError = normalizeError(error);

  // Handle errors based on their category
  switch (normalizedError.category) {
    case ErrorCategory.AUTHENTICATION:
      console.error('Authentication error:', normalizedError.message);
      // Could add redirect to login or token refresh here
      break;

    case ErrorCategory.SERVER:
      console.error(
        'Server error:',
        normalizedError.message,
        `(Error ID: ${normalizedError.errorId})`,
      );
      break;

    case ErrorCategory.VALIDATION:
      console.error(
        'Validation error:',
        normalizedError.message,
        normalizedError.validationErrors,
      );
      break;

    case ErrorCategory.CLIENT:
      console.error('Client error:', normalizedError.message);
      break;

    case ErrorCategory.NETWORK:
      console.error('Network error:', normalizedError.message);
      break;

    default:
      console.error('Unknown error:', normalizedError.message);
  }

  // Return normalized error for downstream handlers
  return normalizedError;
};

/**
 * Creates a QueryClient with global error listeners
 */
export const createGlobalErrorListener = () => {
  const queryClient = new QueryClient();

  // Subscribe to query cache events
  queryClient.getQueryCache().subscribe((event: QueryCacheNotifyEvent) => {
    // Check if this is an updated event and if there's an error in the query state
    if (event.type === 'updated' && event.query.state.error) {
      defaultQueryErrorHandler(event.query.state.error);
    }
  });

  // Subscribe to mutation cache events
  queryClient.getMutationCache().subscribe((event: MutationCacheNotifyEvent) => {
    // Check if this is an updated event and if there's an error in the mutation state
    if (event.type === 'updated' && event.mutation.state.error) {
      defaultQueryErrorHandler(event.mutation.state.error);
    }
  });

  return queryClient;
};

/**
 * Creates and configures the application's QueryClient
 * with default options for queries and mutations
 */
export const createQueryClient = () => {
  const queryClient = createGlobalErrorListener();

  queryClient.setDefaultOptions({
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx client errors
        if (
          error instanceof AxiosError &&
          error.response?.status &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false, // Mutations don't retry by default
    },
  });

  return queryClient;
};
